import React, { createContext, useContext, useEffect } from 'react';

// App is dark-only. This provider now simply guarantees the `dark` class is present
// and exposes a stable (no-op) API so existing consumers keep compiling.
type Theme = 'dark';

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

interface ThemeProviderState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: 'dark';
}

const ThemeProviderContext = createContext<ThemeProviderState | undefined>(undefined);

export function ThemeProvider({ children }: ThemeProviderProps) {
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light');
    root.classList.add('dark');
    // Clear any legacy stored preference so nothing tries to re-apply light mode.
    try {
      localStorage.setItem('vite-ui-theme', 'dark');
    } catch (e) {
      // ignore
    }
  }, []);

  const value: ThemeProviderState = {
    theme: 'dark',
    setTheme: () => {
      /* dark-only: theme switching is disabled */
    },
    resolvedTheme: 'dark',
  };

  return (
    <ThemeProviderContext.Provider value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
